/**
 * Composing the filter chips into one Anki search string.
 *
 * The design's free-text field accepts **real Anki syntax** (`tag:leech -is:suspended`), and the
 * chips above it compile to the same language rather than to a parallel set of typed filters. One
 * query language, one place it can be wrong — and anything the chips cannot express, you can still
 * type.
 *
 * Semantics are the usual faceted ones: **OR within a facet, AND between facets.** Two state chips
 * mean "either state"; a state chip and a flag chip mean "both".
 */

import { dayMonthMaybeYear } from "@/lib/dates";

export type CardState = "new" | "learning" | "young" | "mature" | "suspended";

export const CARD_STATES: readonly CardState[] = [
  "new",
  "learning",
  "young",
  "mature",
  "suspended",
];

/**
 * What a state is CALLED, as against what it IS.
 *
 * `CardState`'s members do six jobs at once — the `STATE_QUERY` key below, the `cards.state=` URL
 * value, the `data-check="cards.chip.new"` hook, `STATE_STYLE`'s CSS lookup in `CardRow`, and the
 * `state` the server puts on every browse row. **Only the label is copy**, so only the label is
 * here; translating the member itself would rewrite `is:new` to `undefined`, break every saved URL
 * and take `check-nav.mjs` with it.
 *
 * The words are the design canvas's (`stateChips: ['新規','学習中','若い','熟成','停止']`) with one
 * deliberate change, Alex's call: **未習熟 / 習熟 rather than 若い / 熟成**, which are Anki's own
 * Japanese for the two interval bands. Stats draws the same two concepts a dozen times
 * (未習熟の正答率, 習熟の最大), so the app names one thing one way on every screen — which is the
 * whole reason `docs/CHROME.md` exists. 新規 agrees with Anki already.
 */
export const STATE_LABEL: Record<CardState, string> = {
  new: "新規",
  learning: "学習中",
  young: "未習熟",
  mature: "習熟",
  suspended: "停止",
};

/**
 * Anki has no `is:young` / `is:mature`; both are review cards split on interval, at the same 21-day
 * boundary the backend uses for the state pill.
 */
const STATE_QUERY: Record<CardState, string> = {
  new: "is:new",
  learning: "is:learn",
  // -is:suspended matters: `is:review` matches by TYPE, so a suspended review card would
  // otherwise answer the young/mature chips while its state pill says "suspended".
  young: "(is:review AND prop:ivl<21 AND -is:suspended)",
  mature: "(is:review AND prop:ivl>=21 AND -is:suspended)",
  suspended: "is:suspended",
};

/**
 * Anki's five user flags, in its own order. Flag 0 is "no flag" and is not offered as a filter.
 *
 * `label` is copy; `value` is the wire value and `token` the CSS custom property, so the colour
 * words translate freely. **`NO_FLAG` is here rather than written out again at the two call sites**
 * that need a "clear it" entry — it was an inline literal in both `Cards.tsx`'s row menu and its
 * bulk bar, which is two copies of one word waiting to disagree.
 */
export const FLAGS = [
  { value: 1, label: "赤", token: "--flag-red" },
  { value: 2, label: "橙", token: "--flag-orange" },
  { value: 3, label: "緑", token: "--flag-green" },
  { value: 4, label: "青", token: "--flag-blue" },
  { value: 5, label: "桃", token: "--flag-pink" },
] as const;

export const NO_FLAG = { value: 0, label: "なし", token: "--flag-none" } as const;

/**
 * A flag control's accessible name — the ONE phrasing, for all four call sites.
 *
 * The swatches are pure colour, so the only thing a screen reader has to go on is this string, and
 * it was three different strings for one idea: `Red flag` in the filter bar, `Flag Red` in the bulk
 * bar and — in both of `CardRow`'s stripes — `` `Flag ${row.flag}` ``, which interpolates the wire
 * NUMBER and announced "Flag 3". Reading the label out of `FLAGS` is what stops a fourth spelling
 * appearing, and what makes a renamed colour reach every control at once.
 *
 * `フラグなし` rather than `なしフラグ` for flag 0 — it is Review's own word for the same target
 * (`Session.tsx`'s sixth flag row), and the compositional form does not survive the absence of a
 * colour.
 */
export function flagName(value: number): string | null {
  if (value === NO_FLAG.value) return "フラグなし";
  const found = FLAGS.find((f) => f.value === value);
  return found ? `${found.label}フラグ` : null;
}

/** Escape a deck or tag name for use inside a quoted Anki search term. */
export function quote(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function anyOf(clauses: string[]): string | null {
  if (clauses.length === 0) return null;
  if (clauses.length === 1) return clauses[0]!;
  return `(${clauses.join(" OR ")})`;
}

export interface CardFilters {
  /** null means every deck — the `All` segment. */
  deck: string | null;
  states: CardState[];
  flags: number[];
  tags: string[];
  /** Typed verbatim by the user; appended untouched so any Anki syntax works. */
  text: string;
}

export const EMPTY_FILTERS: CardFilters = {
  deck: null,
  states: [],
  flags: [],
  tags: [],
  text: "",
};

/**
 * Build the search.
 *
 * Returns `""` when nothing at all is selected — the API treats an empty query as "return nothing"
 * rather than "hydrate the collection", so an unfiltered `All` deliberately shows no rows.
 */
export function buildQuery(filters: CardFilters): string {
  const clauses: string[] = [];

  if (filters.deck) clauses.push(`deck:"${quote(filters.deck)}"`);

  const states = anyOf(filters.states.map((s) => STATE_QUERY[s]));
  if (states) clauses.push(states);

  const flags = anyOf(filters.flags.map((f) => `flag:${f}`));
  if (flags) clauses.push(flags);

  const tags = anyOf(filters.tags.map((t) => `tag:"${quote(t)}"`));
  if (tags) clauses.push(tags);

  const text = filters.text.trim();
  // Parenthesised: free text may itself contain an OR, and unwrapped it would bind loosely enough
  // to swallow the deck clause — `deck:X AND a OR b` is not what the chips above it promised.
  if (text) clauses.push(`(${text})`);

  return clauses.join(" AND ");
}

/**
 * True when the filters would return the whole deck — used to explain an empty list.
 *
 * Takes everything but the deck, because the deck is not a filter here: "this deck is empty" and
 * "nothing matches these filters" are the two answers, and which deck you are in is the premise of
 * both. That also lets the URL's wider `deck` type pass straight through.
 */
export function isUnfiltered(filters: Omit<CardFilters, "deck">): boolean {
  return (
    filters.states.length === 0 &&
    filters.flags.length === 0 &&
    filters.tags.length === 0 &&
    filters.text.trim() === ""
  );
}

/** Toggle a value in a facet, preserving order so chips do not jump as you click them. */
export function toggle<T>(values: readonly T[], value: T): T[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

/**
 * The design's Due column: a date for scheduled cards, `今日`, `—` for suspended, and for a new
 * card its **position in the new queue** rather than a date.
 *
 * **The DATE is Japanese too** (`8月4日`, `2027年8月4日`), which reverses what this comment used to
 * claim. THEME.md's closed exclusion list does name dates, and the canvas draws `9 Aug` in this
 * very column — Alex overruled both on 15 Aug 2026, and `lib/dates.ts` carries the argument: a date
 * was the one piece of English left inside otherwise wholly Japanese rows, and `8月4日` names its
 * own units so the day-first/month-first ambiguity that module exists to remove is removed harder
 * rather than reintroduced. The consequence for this column is the 104px width `CardRow.tsx`
 * measures beside it; the old 74px box did not fit the English form either.
 */
export function dueLabel(
  row: { state: string; dueDate: string | null; duePosition: number | null },
  today: Date = new Date(),
): string {
  if (row.state === "suspended") return "—";
  // `新規 #1,204` is the canvas's own form for this cell, number and all.
  if (row.duePosition !== null) return `新規 #${row.duePosition.toLocaleString()}`;
  if (!row.dueDate) return "—";

  const due = new Date(`${row.dueDate}T00:00:00`);
  const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((due.getTime() - midnight.getTime()) / 86_400_000);
  if (days === 0) return "今日";
  if (days < 0) return `${Math.abs(days)}日前`;
  // Carries the year when it is not this one. A mature card 14 months out used to render `Aug 4`,
  // which reads as next week.
  return dayMonthMaybeYear(due, today);
}
